import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import ICensusReviewPageStore, { CensusReviewDomainParams } from 'store/modules/census/iCensusReviewPageStore';
import { OverlaySpinner } from '@insurancemenu/imenu-components-lib';
import {
  ButtonsWrapperStyled,
  FilterCardQuantityStyled,
  FilterCardTitleStyled,
  ImportErrorsCardStyled,
  StatisticsBlockStyled,
  StatisticsCardStyled,
  SubmitButtonDisclaimerStyled,
  ActionButtonStyled,
  SpinnerWrapperStyled,
} from './styles';
import CensusTable from './CensusTable';
import IncompatibilityError from '../IncompatibilityError';
import {
  CENSUS_STATISTICS_REDO_BUTTON_TEST_ID,
  CENSUS_STATISTICS_SUBMIT_BUTTON_TEST_ID,
  CENSUS_STATISTICS_BLOCK_TEST_ID,
  CENSUS_STATISTICS_TOTAL_CARD_TEST_ID,
  CENSUS_STATISTICS_EMPLOYEES_CARD_TEST_ID,
  CENSUS_STATISTICS_DEPENDENTS_CARD_TEST_ID,
  CENSUS_STATISTICS_ERRORS_CARD_TEST_ID,
  CENSUS_STATISTICS_ERROR_DISCLAIMER_TEST_ID,
  CENSUS_STATISTICS_LOADER_TEST_ID,
} from '../constants';

interface CensusStatsProps {
  pageStore: ICensusReviewPageStore<CensusReviewDomainParams>;
}

const CensusStatistics: React.FC<CensusStatsProps> = observer(({ pageStore }) => {
  const { statistics, isLoading, hasErrors, incompatibilityErrorMsg, getStatistics, onCensusSubmit, onBackRedirect } =
    pageStore;

  useEffect(() => {
    void getStatistics();
  }, []);

  if (isLoading) {
    return (
      <SpinnerWrapperStyled>
        <OverlaySpinner display data-test-id={CENSUS_STATISTICS_LOADER_TEST_ID} />
      </SpinnerWrapperStyled>
    );
  }

  return incompatibilityErrorMsg ? (
    <IncompatibilityError pageStore={pageStore} />
  ) : (
    <>
      <StatisticsBlockStyled data-test-id={CENSUS_STATISTICS_BLOCK_TEST_ID}>
        <StatisticsCardStyled data-test-id={CENSUS_STATISTICS_TOTAL_CARD_TEST_ID}>
          <FilterCardQuantityStyled>{statistics.total}</FilterCardQuantityStyled>
          <FilterCardTitleStyled>Total Imports</FilterCardTitleStyled>
        </StatisticsCardStyled>
        <StatisticsCardStyled data-test-id={CENSUS_STATISTICS_EMPLOYEES_CARD_TEST_ID}>
          <FilterCardQuantityStyled>{statistics.employees}</FilterCardQuantityStyled>
          <FilterCardTitleStyled>Employees Imported</FilterCardTitleStyled>
        </StatisticsCardStyled>
        <StatisticsCardStyled data-test-id={CENSUS_STATISTICS_DEPENDENTS_CARD_TEST_ID}>
          <FilterCardQuantityStyled>{statistics.dependents}</FilterCardQuantityStyled>
          <FilterCardTitleStyled>Dependents Imported</FilterCardTitleStyled>
        </StatisticsCardStyled>
        <ImportErrorsCardStyled data-test-id={CENSUS_STATISTICS_ERRORS_CARD_TEST_ID}>
          <FilterCardQuantityStyled>{statistics.errors}</FilterCardQuantityStyled>
          <FilterCardTitleStyled>Import Errors</FilterCardTitleStyled>
        </ImportErrorsCardStyled>
      </StatisticsBlockStyled>
      <ButtonsWrapperStyled>
        <ActionButtonStyled
          onClick={onBackRedirect}
          type="outlined"
          color="primary"
          size="large"
          data-test-id={CENSUS_STATISTICS_REDO_BUTTON_TEST_ID}
        >
          Redo this import
        </ActionButtonStyled>
        <div>
          <ActionButtonStyled
            disabled={hasErrors || isLoading}
            onClick={onCensusSubmit}
            color="primary"
            size="large"
            data-test-id={CENSUS_STATISTICS_SUBMIT_BUTTON_TEST_ID}
          >
            Submit Census
          </ActionButtonStyled>
          {hasErrors && (
            <SubmitButtonDisclaimerStyled data-test-id={CENSUS_STATISTICS_ERROR_DISCLAIMER_TEST_ID}>
              Correct errors before submitting
            </SubmitButtonDisclaimerStyled>
          )}
        </div>
      </ButtonsWrapperStyled>
      <CensusTable pageStore={pageStore} />
    </>
  );
});

export default CensusStatistics;
